import { useEffect, useState } from 'react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import {
  clearReloadStamp,
  isChunkLoadError,
  markReloadAttempt,
  shouldAutoReloadNow,
} from '../lib/routeError';
import '../features/auth/auth.css';

/**
 * Router-level error screen (root errorElement) — replaces React Router's developer page.
 * Deliberately independent of AppShell and AuthProvider: it must render when everything else
 * is broken, including before auth state exists. Never renders the error itself — fixed copy
 * only (the router's default page prints error messages and, in dev, stacks).
 */
export function RouteErrorPage() {
  const error = useRouteError();

  // Set when the reload stamp can't be persisted — without the loop-brake an automatic
  // reload is unsafe, so fall through to the "can't reach" screen instead.
  const [reloadBlocked, setReloadBlocked] = useState(false);

  const notFound = isRouteErrorResponse(error) && error.status === 404;
  const chunkFailed = !notFound && isChunkLoadError(error);
  const autoReload = chunkFailed && !reloadBlocked && shouldAutoReloadNow(Date.now());

  useEffect(() => {
    if (!autoReload) {
      return;
    }
    // A deploy replaced the hashed chunk this (stale) tab asked for — one reload fetches the
    // new index.html and fixes it. Stamp BEFORE reloading so a dead server can't reload-loop.
    if (markReloadAttempt(Date.now())) {
      window.location.reload();
    } else {
      setReloadBlocked(true);
    }
  }, [autoReload]);

  useEffect(() => {
    if (!notFound && !autoReload) {
      // Custom errorElement suppresses the router's own logging — keep the detail reachable
      // for support without putting it on the screen.
      console.error(error);
    }
  }, [error, notFound, autoReload]);

  if (notFound) {
    return (
      <div className="auth-page">
        <Card className="auth-card" title="Page not found">
          <div className="auth-info">
            That page doesn&apos;t exist — the address may have been mistyped, or the link is out
            of date.
          </div>
          <Link className="auth-inline-link" to="/">
            Go to the portal
          </Link>
        </Card>
      </div>
    );
  }

  if (autoReload) {
    return (
      <div className="auth-page">
        <Card className="auth-card" title="Updating">
          <div className="auth-info">Loading the latest version of the portal…</div>
          <Spinner center />
        </Card>
      </div>
    );
  }

  if (chunkFailed) {
    return (
      <div className="auth-page">
        <Card className="auth-card" title="Can't reach the portal">
          <div className="auth-info">
            The portal didn&apos;t respond — it may be briefly offline, or your connection may
            have dropped. Nothing you&apos;ve done has been lost.
          </div>
          <Button
            onClick={() => {
              clearReloadStamp();
              window.location.reload();
            }}
          >
            Try again
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <Card className="auth-card" title="Something went wrong">
        <div className="auth-info">
          An unexpected error occurred. Reloading usually fixes it — if it keeps happening, let
          the club admin know.
        </div>
        <Button onClick={() => window.location.reload()}>Reload</Button>
        <Link className="auth-inline-link" to="/">
          Go to the portal
        </Link>
      </Card>
    </div>
  );
}
